import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

const DateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    className="flex h-10 w-full items-center justify-between gap-2 rounded-xl bg-white/[0.04] px-3 text-sm text-white outline-none transition hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-brand-400"
  >
    <span className={`flex-1 text-left ${value ? 'text-white' : 'text-muted-500'}`}>
      {value || placeholder}
    </span>
    <span className="material-icons text-base text-muted-400">calendar_today</span>
  </button>
))

DateInput.displayName = 'DateInput'

const FiltersSection = ({
  schoolList = [],
  selectedSchool = '',
  onSchoolChange,
  startDate = null,
  onStartDateChange,
  endDate = null,
  onEndDateChange,
  onApplyFilters,
  onResetFilters,
  isFiltered = false,
  datePickerPortalId = 'filters-datepicker-portal',
  calendarClassName = 'logs-datepicker',
  popperClassName = 'logs-datepicker-popper',
}) => {
  const { t } = useTranslation();
  const [schoolSearchQuery, setSchoolSearchQuery] = useState(selectedSchool || '')
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const searchInputRef = useRef(null)

  // Фильтрация школ по поисковому запросу
  const filteredSchools = useMemo(() => {
    if (!schoolSearchQuery.trim()) return schoolList.slice(0, 10) // Показываем первые 10, если нет поиска
    const query = schoolSearchQuery.toLowerCase()
    return schoolList.filter(school => 
      school.toLowerCase().includes(query)
    ).slice(0, 10)
  }, [schoolSearchQuery, schoolList])

  const handleSchoolSelect = (school) => {
    setSchoolSearchQuery(school)
    onSchoolChange(school)
    setShowSchoolDropdown(false)
  }

  const handleSchoolSearchChange = (e) => {
    setSchoolSearchQuery(e.target.value)
    setShowSchoolDropdown(true)
    if (!e.target.value) {
      onSchoolChange('')
    }
  }

  // Синхронизируем searchQuery с selectedSchool при изменении извне
  React.useEffect(() => {
    if (selectedSchool !== schoolSearchQuery) {
      setSchoolSearchQuery(selectedSchool || '')
    }
  }, [selectedSchool])

  return (
    <section className="relative grid grid-cols-1 gap-4 overflow-visible rounded-3xl bg-surface-1 p-6 md:grid-cols-4">
      <label className="relative flex flex-col gap-2 overflow-visible text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
        {t('filters.object')}
        <div className="relative overflow-visible">
          <input
            type="text"
            value={schoolSearchQuery}
            onChange={handleSchoolSearchChange}
            ref={searchInputRef}
            onFocus={(e) => {
              const rect = e.target.getBoundingClientRect()
              setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width,
              })
              setShowSchoolDropdown(true)
            }}
            onBlur={() => setTimeout(() => setShowSchoolDropdown(false), 200)}
            placeholder={t('filters.searchByName')}
            className="h-10 w-full rounded-xl bg-white/[0.04] px-3 pl-10 text-sm text-white outline-none placeholder:text-muted-500 focus-visible:ring-2 focus-visible:ring-brand-400"
          />
          <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-400">search</span>
          {showSchoolDropdown && filteredSchools.length > 0 && createPortal(
            <div
              className="fixed z-[9999] max-h-60 overflow-auto rounded-2xl bg-surface-2"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              {filteredSchools.map((school) => (
                <button
                  key={school}
                  type="button"
                  onClick={() => handleSchoolSelect(school)}
                  className="w-full px-4 py-2.5 text-left text-sm text-muted-100 transition hover:bg-white/[0.06] hover:text-white"
                >
                  {school}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
      </label>

      <label className="flex flex-col gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
        {t('filters.startPeriod')}
        <DatePicker
          selected={startDate}
          onChange={onStartDateChange}
          dateFormat="dd.MM.yyyy"
          placeholderText={t('filters.selectDate')}
          customInput={<DateInput placeholder={t('filters.startPeriod')} />}
          wrapperClassName="w-full"
          calendarClassName={calendarClassName}
          popperClassName={popperClassName}
          portalId={datePickerPortalId}
          minDate={new Date('2020-01-01')}
          maxDate={endDate || new Date()}
          isClearable
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          scrollableYearDropdown
          startYear={2020}
          endYear={new Date().getFullYear()}
          className="w-full"
        />
      </label>

      <label className="flex flex-col gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
        {t('filters.endPeriod')}
        <DatePicker
          selected={endDate}
          onChange={onEndDateChange}
          dateFormat="dd.MM.yyyy"
          placeholderText={t('filters.selectDate')}
          customInput={<DateInput placeholder={t('filters.endPeriod')} />}
          wrapperClassName="w-full"
          calendarClassName={calendarClassName}
          popperClassName={popperClassName}
          portalId={datePickerPortalId}
          minDate={startDate || new Date('2020-01-01')}
          maxDate={new Date()}
          isClearable
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          scrollableYearDropdown
          startYear={2020}
          endYear={new Date().getFullYear()}
          className="w-full"
        />
      </label>

      <div className="flex flex-col justify-end gap-2 md:items-end">
        <div className="inline-flex items-center gap-2 text-xs text-muted-400">
          <span className={`h-1.5 w-1.5 rounded-full ${isFiltered ? 'bg-positive' : 'bg-muted-500'}`} />
          {isFiltered ? t('filters.filtersApplied') : t('filters.defaultFilters')}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onApplyFilters}
            className="inline-flex items-center gap-1.5 rounded-xl bg-white/[0.08] px-3 py-2 text-xs font-medium text-white transition hover:bg-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <span className="material-icons text-sm">tune</span>
            {t('filters.apply')}
          </button>
          <button
            type="button"
            onClick={onResetFilters}
            className="inline-flex items-center gap-1.5 rounded-xl bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-300 transition hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <span className="material-icons text-sm">restart_alt</span>
            {t('filters.reset')}
          </button>
        </div>
      </div>
    </section>
  )
}

export default FiltersSection

